/**
 * spaceConstants.ts
 * Tunable constants for the World 2 space shoot-em-up scene.
 *
 * Phase 4 — World 2: Starfield Drift.
 * See ADR-009 and ROADMAP.md Phase 4 "Space Engine".
 *
 * @module spaceConstants
 */

// ── Ship dimensions ──────────────────────────────────────────────────────────

/**
 * Logical hitbox width of the Courier Ship in pixels.
 * Tighter than the 125×63 sprite so collisions feel fair — the sprite has
 * transparent padding around the hull silhouette. Matches the `md`
 * asteroid (32 px) on the horizontal axis and is well under an `xl`
 * asteroid (64 px); the much smaller {@link SHIP_HEIGHT} is what
 * keeps dense fields dodgeable on the dominant (vertical) axis.
 */
export const SHIP_WIDTH = 32;

/**
 * Logical hitbox height of the Courier Ship in pixels.
 * Half the `md` asteroid (32 px) and equal to an `sm` asteroid (16 px),
 * so vertical-dodge gaps that look traversable on screen actually are.
 */
export const SHIP_HEIGHT = 16;

/** Starting hit points for a fresh ship. */
export const SHIP_MAX_HP = 3;

/**
 * Seconds of post-hit invulnerability ("iframes") granted to the ship
 * after taking damage. Tuned long enough that the player can cleanly
 * disengage from a cluster of hazards (mine swarm, boss-projectile
 * spread) without taking back-to-back hits in a single tick, but short
 * enough that camping inside a hazard still drains HP.
 */
export const SHIP_INVULN_DURATION_S = 1.5;

/**
 * Iframe blink frequency in Hz. The renderer toggles the ship between
 * the default fill and the hit-flash palette twice per cycle, so a
 * 12 Hz rate produces 24 state changes per second — each visual state
 * lasts ~2.5 frames at 60 fps, fast enough to read as "I'm
 * invulnerable" but slow enough to be clearly legible and not strobe.
 */
export const SHIP_IFRAME_BLINK_RATE_HZ = 12;

/** HP removed per ship-vs-homing-mine contact. */
export const SHIP_DAMAGE_PER_MINE = 1;

/** HP removed per ship-vs-boss-turret-bolt contact. */
export const SHIP_DAMAGE_PER_BOSS_PROJECTILE = 1;

/** HP removed per ship-vs-boss-missile contact. */
export const SHIP_DAMAGE_PER_BOSS_MISSILE = 1;

/** HP removed per ship-vs-boss-body contact (ram / overlap). */
export const SHIP_DAMAGE_PER_BOSS_BODY = 1;

/** HP removed per ship-vs-asteroid contact. */
export const SHIP_DAMAGE_PER_ASTEROID = 1;

/**
 * Leftward push (px / s) applied to the ship's `vx` when it rams an
 * indestructible asteroid (`lg` / `xl`). Negative pushes the ship away
 * from the rock and back toward the player's safe zone. Destructible
 * asteroids (`sm` / `md`) are consumed on contact and do NOT bounce
 * the ship — the smash is the reward for taking the hit.
 *
 * DECISION: Applied as an absolute velocity overwrite, not an additive
 * impulse, so a stream of overlap ticks against the same rock can't
 * compound the ship's vx into a runaway leftward escape.
 */
export const SHIP_BOUNCE_VELOCITY_PX_S = -240;

/** HP removed per ship-vs-enemy-body contact (fighter / bomber / shield satellite ram). */
export const SHIP_DAMAGE_PER_ENEMY = 1;

/** HP removed per ship-vs-fighter-drone-laser contact. */
export const SHIP_DAMAGE_PER_ENEMY_LASER = 1;

/** HP removed per ship-vs-bomber-cluster-bomb contact (the parent
 *  payload, not the post-fuse shrapnel). */
export const SHIP_DAMAGE_PER_BOMB = 1;

/** HP removed per ship-vs-bomb-shrapnel contact (each individual
 *  fragment from a detonated cluster bomb). */
export const SHIP_DAMAGE_PER_SHRAPNEL = 1;

// ── Ship movement ────────────────────────────────────────────────────────────

/**
 * Acceleration applied each second when a directional input is held.
 * Tuned so the ship reaches `SHIP_MAX_SPEED` in roughly 0.15 s — responsive
 * but not snap-instant, which gives the player a sense of inertia consistent
 * with piloting a ship rather than steering a platformer character.
 */
export const SHIP_ACCELERATION = 2400;

/**
 * Deceleration applied each second along an axis when no input is held on
 * that axis. Higher than gravity-free coasting would suggest because the
 * R-Type / Gradius lineage favours precise positioning over momentum.
 */
export const SHIP_FRICTION = 2400;

/** Maximum scalar speed in either axis (px / s). */
export const SHIP_MAX_SPEED = 320;

// ── Auto-scroll ─────────────────────────────────────────────────────────────

/**
 * Speed at which the space camera scrolls right (px / s).
 * Constant — auto-scroll levels never slow down or stop unless a boss arena
 * clamps the camera (handled in a later PR).
 */
export const SPACE_AUTO_SCROLL_SPEED = 80;

/**
 * Seconds of "outro grace" between meeting a level's end-condition
 * (duration elapsed / boss defeated) and `spaceState.levelComplete`
 * flipping to `true`. The step function uses this to keep ticking
 * existing entities (asteroids drift off-screen, boss settles into
 * `DEFEATED`) while spawners stay off, so the Sector Cleared overlay
 * doesn't land on a busy frame.
 *
 * DECISION (W2 overhaul): User feedback called out that levels end
 * abruptly. 1.5 s is long enough to read as a beat without dragging.
 */
export const SPACE_LEVEL_OUTRO_GRACE_S = 1.5;
// ── Ship projectiles ───────────────────────────────────────────────────

/** Hitbox width of a ship-fired projectile (logical px). */
export const SHIP_PROJECTILE_WIDTH = 10;

/** Hitbox height of a ship-fired projectile (logical px). */
export const SHIP_PROJECTILE_HEIGHT = 4;

/**
 * Travel speed of ship-fired projectiles in px / s.
 * Faster than the platformer Star Shard (400) — the auto-scroll camera adds
 * apparent backward drag, so projectiles need extra forward velocity to feel
 * snappy on screen.
 */
export const SHIP_PROJECTILE_SPEED = 540;

/**
 * Maximum pooled ship projectiles. AGENTS.md caps pools at 50.
 * At SHIP_PROJECTILE_COOLDOWN_S=0.15 (~6.7 shots/s) and SHIP_PROJECTILE_TTL_S=1.0,
 * the steady-state in-flight count is ~7. 20 gives nearly 3× headroom for
 * future spreads / homing variants without ever exhausting the pool in normal play.
 */
export const SHIP_MAX_PROJECTILES = 20;

/**
 * Seconds between consecutive shots. ~6.7 shots/sec at 0.15 — fast enough
 * to feel like a continuous stream when the player holds Attack, slow enough
 * not to instantly exhaust the pool.
 */
export const SHIP_PROJECTILE_COOLDOWN_S = 0.15;

/**
 * Maximum lifetime of a ship projectile in seconds.
 * 1 s at SHIP_PROJECTILE_SPEED = 540 px of travel, safely past the 480 px
 * viewport — ensures projectiles that miss leave the screen before despawn.
 */
export const SHIP_PROJECTILE_TTL_S = 1.0;

// ── Ship missiles (secondary weapon) ─────────────────────────────────

/**
 * Seconds the Attack input must be held continuously before the secondary
 * missile fires. Below this threshold the input only spawns laser shots
 * via the primary auto-fire path — quick taps stay clean, deliberate
 * holds get rewarded with a missile.
 */
export const SHIP_MISSILE_HOLD_THRESHOLD_S = 0.5;

/** Hitbox width of a ship-fired missile (logical px). */
export const SHIP_MISSILE_WIDTH = 18;

/** Hitbox height of a ship-fired missile (logical px). */
export const SHIP_MISSILE_HEIGHT = 8;

/**
 * Travel speed of a missile in px / s. Roughly half the laser speed so
 * the missile reads as a heavier, more deliberate projectile and the
 * player has a beat to see it travel before it detonates.
 */
export const SHIP_MISSILE_SPEED = 280;

/**
 * Maximum pooled ship missiles. Much smaller than the laser pool — the
 * 1.2 s cooldown and one-shot-per-hold gesture mean the steady-state
 * in-flight count is at most 1–2.
 */
export const SHIP_MAX_MISSILES = 4;

/**
 * Seconds between consecutive missile shots. Long enough that the
 * missile feels weighty (vs the laser stream) but short enough that a
 * player who deliberately re-holds Attack gets a steady rhythm.
 */
export const SHIP_MISSILE_COOLDOWN_S = 1.2;

/**
 * Maximum lifetime of a ship missile in seconds.
 * 2 s at SHIP_MISSILE_SPEED = 560 px of travel, past the 480 px viewport.
 */
export const SHIP_MISSILE_TTL_S = 2.0;

/**
 * Direct-hit damage applied to the asteroid / enemy / boss the missile
 * physically overlaps. Three times the laser's 1-HP per shot — the
 * missile is the heavy single-shot, the laser is the light DPS stream.
 */
export const SHIP_MISSILE_DIRECT_DAMAGE = 3;

/**
 * Splash damage applied to OTHER live entities within
 * `SHIP_MISSILE_AOE_RADIUS_PX` of the impact point. Lighter than the
 * direct hit so a missile that lands in a cluster reads as "hit + chip
 * the neighbours", not "wipe the cluster".
 */
export const SHIP_MISSILE_AOE_DAMAGE = 1;

/**
 * Radius (logical px) within which splash damage applies, measured
 * from the impact point to each candidate's centre.
 */
export const SHIP_MISSILE_AOE_RADIUS_PX = 56;

// ── Camera shake ─────────────────────────────────────────────────────

/**
 * Shake magnitude applied when the ship takes a hit (logical px). Small
 * enough to read as a "thump" without making readouts unreadable.
 */
export const SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX = 4;

/** Shake duration applied when the ship takes a hit (seconds). */
export const SPACE_SHAKE_DURATION_SHIP_HIT_S = 0.2;

/**
 * Shake magnitude applied when a missile detonates (logical px). The
 * missile's own kill VFX already carries most of the impact; the shake
 * just adds physical weight.
 */
export const SPACE_SHAKE_MAGNITUDE_MISSILE_PX = 3;

/** Shake duration applied when a missile detonates (seconds). */
export const SPACE_SHAKE_DURATION_MISSILE_S = 0.15;

/**
 * Shake magnitude applied when the Void Mothership ignites a ram
 * charge (TELEGRAPH → CHARGE). Sits between the missile bump (3) and
 * the ship-hit thump (4) — it's a boss attack-pattern telegraph, so
 * it should read heavier than the player's own missile detonation but
 * NOT louder than the player actually being hit. Pair with
 * {@link SPACE_SHAKE_DURATION_BOSS_RAM_S}.
 */
export const SPACE_SHAKE_MAGNITUDE_BOSS_RAM_PX = 3.5;

/** Shake duration applied at boss ram ignition (seconds). */
export const SPACE_SHAKE_DURATION_BOSS_RAM_S = 0.2;